import type { EntityType, InvestmentStage } from './types';

const stageToZh: Record<string, InvestmentStage> = {
  INITIAL_CONTACT: '初步接洽',
  EVALUATION: '项目研判',
  NEGOTIATION: '考察洽谈',
  PROPOSAL_NEGOTIATION: '方案谈判',
  DECISION_SIGNING: '决策签约',
  LANDED: '已转落地',
  PAUSED: '暂缓',
  ABANDONED: '放弃',
};

export const stageToCode = Object.fromEntries(
  Object.entries(stageToZh).map(([code, label]) => [label, code]),
) as Record<InvestmentStage, string>;

const valueToZh: Record<string, string> = {
  NEW: '新线索',
  FOLLOWING: '跟进中',
  CONVERTED: '已转化',
  PAUSED: '暂缓',
  INVALID: '无效',
  HIGH: '高',
  MEDIUM: '中',
  LOW: '低',
  TARGET: '目标企业',
  CONTACTED: '已接洽企业',
  SIGNED: '签约企业',
  LANDED: '落地企业',
  SIGNING_PREPARATION: '签约准备',
  REGISTRATION: '注册落户',
  SITE_APPROVAL: '选址与审批',
  CONSTRUCTION: '建设实施',
  PRODUCTION: '投产运营',
  COMPLETED: '已完成',
  PENDING: '待处理',
  IN_PROGRESS: '进行中',
  PROCESSING: '处理中',
  PENDING_CONFIRMATION: '待企业确认',
  UNCONTACTED: '未联系',
  INITIAL_CONTACT: '初步接洽',
  COMMUNICATED: '已沟通',
  CONTACTED_RESOURCE: '已联系',
  OPEN_FOR_REGISTRATION: '待报名',
  UPCOMING: '待参加',
  REGISTERED: '已报名',
  ENDED: '已结束',
};

const valueToCode = Object.fromEntries(
  Object.entries(valueToZh).map(([code, label]) => [label, code]),
) as Record<string, string>;

export const toLabel = (value: unknown) =>
  typeof value === 'string'
    ? stageToZh[value] || valueToZh[value] || value
    : value;

export const toCode = (value: unknown) =>
  typeof value === 'string'
    ? stageToCode[value as InvestmentStage] || valueToCode[value] || value
    : value;

export const resourceForType: Record<EntityType, string> = {
  activities: 'activities',
  leads: 'leads',
  enterprises: 'enterprises',
  contacts: 'contacts',
  investmentProjects: 'investment-projects',
  landingProjects: 'landing-projects',
  enterpriseServices: 'enterprise-services',
  carriers: 'resources',
  policyFunds: 'resources',
  cooperationResources: 'resources',
  tasks: 'tasks',
};

export const resourceTypeForEntity: Partial<Record<EntityType, string>> = {
  carriers: 'CARRIER',
  policyFunds: 'POLICY',
  cooperationResources: 'COOPERATION',
};

export const normalizeRecord = (record: Record<string, any>) => ({
  ...record,
  id: record.uuid,
  date: record.startAt,
  sourceId: record.sourceUuid,
  enterpriseId: record.enterpriseUuid,
  contactId: record.contactUuid,
  projectId: record.projectUuid,
  sourceProjectId: record.sourceProjectUuid,
  sourceLandingProjectId: record.sourceLandingProjectUuid,
  landingProjectId: record.landingProjectUuid,
  targetId: record.targetUuid,
  owner: record.owner?.displayName || record.ownerMemberUuid || '',
  status: toLabel(record.status),
  stage: toLabel(record.stage),
  priority: toLabel(record.priority),
  urgency: toLabel(record.urgency),
  risk: toLabel(record.risk),
  relationshipStatus: toLabel(record.relationshipStatus),
  contactStatus: toLabel(record.contactStatus),
  amount: record.amount == null ? 0 : Number(record.amount),
  score: record.score == null ? 0 : Number(record.score),
  matchScore: record.matchScore == null ? 0 : Number(record.matchScore),
  summary: record.description || '',
  tags: record.tags || [],
  leadIds: record.leadIds || [],
  contactIds: record.contactIds || [],
  projectIds: record.projectIds || [],
  landingProjectIds: record.landingProjectIds || [],
  serviceIds: record.serviceIds || [],
  needs: record.needs || [],
  matchedResourceIds: record.matchedResourceIds || [],
});

export const serializeRecord = (
  type: EntityType,
  input: Record<string, any>,
) => {
  const result: Record<string, any> = {};
  const ignored = new Set([
    'id',
    'uuid',
    'createdAt',
    'updatedAt',
    'owner',
    'tags',
    'leadIds',
    'contactIds',
    'projectIds',
    'landingProjectIds',
    'serviceIds',
    'needs',
    'matchedResourceIds',
  ]);
  const keyMap: Record<string, string> = {
    date: 'startAt',
    sourceId: 'sourceUuid',
    enterpriseId: 'enterpriseUuid',
    contactId: 'contactUuid',
    projectId: 'projectUuid',
    sourceProjectId: 'sourceProjectUuid',
    sourceLandingProjectId: 'sourceLandingProjectUuid',
    landingProjectId: 'landingProjectUuid',
    targetId: 'targetUuid',
  };
  for (const [key, value] of Object.entries(input)) {
    if (ignored.has(key) || value === undefined) continue;
    result[keyMap[key] || key] = [
      'status',
      'stage',
      'priority',
      'urgency',
      'risk',
      'relationshipStatus',
      'contactStatus',
    ].includes(key)
      ? toCode(value)
      : value;
  }
  if (input.ownerMemberUuid) result.ownerMemberUuid = input.ownerMemberUuid;
  if (resourceTypeForEntity[type])
    result.resourceType = resourceTypeForEntity[type];
  return result;
};
